import { useModalState } from "@clipboard-health/ui-components";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback } from "react";

import { ReactivationBonusBanner } from "./ReactivationBonusBanner";
import { ReactivationBonusBottomSheet } from "./ReactivationBonusBottomSheet";

const REACTIVATION_BONUS_BANNER_DISMISSED_KEY_PREFIX = "reactivation-bonus-banner-dismissed";

const DEFAULT_REACTIVATION_BONUS_CONFIG = {
  enabled: false,
  bonusAmountInDollars: 0,
  requiredShiftCount: 1,
  startDate: "",
  deadlineDate: "",
};

export function ReactivationBonusEntrypoint() {
  const bottomSheetModalState = useModalState();
  const { userId } = useDefinedWorker();

  const reactivationBonusConfig = useCbhFlag(CbhFeatureFlag.SUPPLY_INCENTIVE_BANNER, {
    defaultValue: DEFAULT_REACTIVATION_BONUS_CONFIG,
  });

  const { enabled, bonusAmountInDollars, requiredShiftCount, startDate, deadlineDate } =
    reactivationBonusConfig;

  const [isDismissed, setIsDismissed] = useLocalStorage(
    `${REACTIVATION_BONUS_BANNER_DISMISSED_KEY_PREFIX}-${userId}-${deadlineDate}`,
    false
  );

  const shouldShowBanner = enabled && !isDismissed;

  useLogEffect(
    APP_V2_USER_EVENTS.REACTIVATION_BONUS_BANNER_VIEWED,
    {
      workerId: userId,
      bonusAmountInDollars,
      requiredShiftCount,
      deadlineDate,
    },
    { enabled: shouldShowBanner }
  );

  const handleBannerClick = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.REACTIVATION_BONUS_BANNER_CLICKED, {
      workerId: userId,
      bonusAmountInDollars,
      requiredShiftCount,
      deadlineDate,
    });
    bottomSheetModalState.openModal();
  }, [userId, bonusAmountInDollars, requiredShiftCount, deadlineDate, bottomSheetModalState]);

  const handleBannerDismiss = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.REACTIVATION_BONUS_BANNER_DISMISSED, {
      workerId: userId,
      bonusAmountInDollars,
      requiredShiftCount,
      deadlineDate,
    });
    setIsDismissed(true);
  }, [userId, bonusAmountInDollars, requiredShiftCount, deadlineDate, setIsDismissed]);

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <>
      <ReactivationBonusBanner
        bonusAmountInDollars={bonusAmountInDollars}
        requiredShiftCount={requiredShiftCount}
        startDate={startDate}
        deadlineDate={deadlineDate}
        onClick={handleBannerClick}
        onDismiss={handleBannerDismiss}
      />
      <ReactivationBonusBottomSheet
        modalState={bottomSheetModalState}
        bonusAmountInDollars={bonusAmountInDollars}
        requiredShiftCount={requiredShiftCount}
        startDate={startDate}
        deadlineDate={deadlineDate}
      />
    </>
  );
}
